import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Observable} from 'rxjs';
import {
    AccessType,
    AuthService,
    DataSources,
    ExportMyScoreFinanceQuery,
    GetMyScoreCreditRequestsScoreQuery,
    GetMyScoreFinanceInvoicesScoreQuery,
    InvoicingClient,
    MyScoreFinanceScoreDto,
    PermissionType,
} from '@services';
import {FileUtils} from '../../../../shared/file-utils';
import {DoughnutChartTableComponent, DoughnutChartTableSlice} from '../../../../components/library/doughnut-chart-table/doughnut-chart-table.component';
import {MyScoreFilterBarComponent, MyScoreFilters} from '../my-score-filter-bar/my-score-filter-bar.component';

type FinanceSection = {
    title: string;
    finalLabel: string;
    load: (filters: MyScoreFilters) => Observable<MyScoreFinanceScoreDto>;
    slices: DoughnutChartTableSlice[];
    score: number | null;
};

@Component({
    selector: 'app-my-score-finance',
    imports: [
        CommonModule,
        DoughnutChartTableComponent,
        MyScoreFilterBarComponent,
    ],
    templateUrl: './my-score-finance.component.html',
})
export class MyScoreFinanceComponent implements OnInit {
    private invoicingClient = inject(InvoicingClient);
    private dataSources = inject(DataSources);
    private auth = inject(AuthService);
    private destroyRef = inject(DestroyRef);

    private static readonly CategoryLabels = {
        pendingUnder48: 'AP Pending <48',
        pendingOver48: 'AP Pending >48',
    };

    protected palette: Record<string, string> = {};

    protected vendorDataSource = this.dataSources.vendorOptions();
    protected siteDataSource = this.dataSources.siteOptions(PermissionType.MyScoreFinanceInvoices);

    protected sections: FinanceSection[] = [];

    private filters?: MyScoreFilters;

    private readonly sectionConfigs: { title: string; permission: PermissionType; finalLabel: string; load: (filters: MyScoreFilters) => Observable<MyScoreFinanceScoreDto> }[] = [
        {
            title: 'Invoices',
            permission: PermissionType.MyScoreFinanceInvoices,
            finalLabel: 'Posted',
            load: filters => this.invoicingClient.getMyScoreFinanceInvoicesScore(new GetMyScoreFinanceInvoicesScoreQuery(filters)),
        },
        {
            title: 'Credit Requests',
            permission: PermissionType.MyScoreCreditRequests,
            finalLabel: 'Completed',
            load: filters => this.invoicingClient.getMyScoreCreditRequestsScore(new GetMyScoreCreditRequestsScoreQuery(filters)),
        },
    ];

    ngOnInit(): void {
        const labels = MyScoreFinanceComponent.CategoryLabels;
        const getColor = (name: string) => getComputedStyle(document.body).getPropertyValue(name).trim();
        this.palette = {
            [labels.pendingUnder48]: getColor('--bs-info'),
            [labels.pendingOver48]: getColor('--bs-warning'),
            'Posted': getColor('--bs-success'),
            'Completed': getColor('--bs-success'),
        };

        this.sections = this.sectionConfigs
            .filter(config => this.auth.checkPermission(config.permission, AccessType.ViewOnly))
            .map(config => ({title: config.title, finalLabel: config.finalLabel, load: config.load, slices: [], score: null}));
    }

    protected onFiltersChanged(filters: MyScoreFilters): void {
        this.filters = filters;
        this.loadScores();
    }

    protected onExport(): void {
        if (!this.filters) return;

        this.invoicingClient
            .exportMyScoreFinance(new ExportMyScoreFinanceQuery(this.filters))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(res => FileUtils.downloadFile(res));
    }

    private loadScores(): void {
        if (!this.filters) return;

        const labels = MyScoreFinanceComponent.CategoryLabels;
        for (const section of this.sections) {
            section.load(this.filters)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(dto => {
                    section.slices = [
                        {argument: labels.pendingUnder48, value: dto.pendingUnder48 ?? 0},
                        {argument: labels.pendingOver48, value: dto.pendingOver48 ?? 0},
                        {argument: section.finalLabel, value: dto.finalized ?? 0},
                    ];
                    section.score = dto.myScorePercent ?? null;
                });
        }
    }
}
